import { useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import BookingFilter from "./BookingFilter";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import styled from "styled-components";
import DeleteBooking from "../DeleteBooking";
import MyBookingMultiDeleteDialog from "../booking-instruments/my-bookings/my-booking-list-view/my-booking-multi-delete-button/MyBookingMultiDeleteDialog";
import BookingNotificationStatus from "../booking-calendars/booking-notification-status/BookingNotificationStatus";
import { timeoutCallbackFactory } from "../booking-calendars/booking-notification-status/timeoutCallbackFactory";
import {
  GRID_VIEW,
  INVALID_FILTER,
  LIST_VIEW,
  LIST_GROUP_BY_BOOKING,
  LIST_GROUP_BY_EQUIPMENT,
  LIST_GROUP_BY_NONE,
  MY_LAB,
  DURATION,
  DEFAULT_COLUMNS,
  DEFAULT_FILTER,
  allTableColumn,
  EQUIPMENT_BASED_DEFAULT_SHOW_COLUMNS,
  TIMELINE_VIEW,
  FORCE_TIMELINE_RANGE_KEY,
  GROUP_VIEW_PREFILL_STORAGE_KEY,
} from "../../constants";
import { filter, find, keyBy, sortBy } from "lodash";
import { useBookingOperations, useBookingSubscriptionStatus, BOOKING_OPERATIONS } from "../booking-operations";
import { quickEditTracker } from "../booking-operations/quickEditTracker";
import { PaginationContext } from "../../components/shared/pagination/PaginationContext";
import { useSelector } from "react-redux";
import { convertStrToObjColumns, convertToCameCase, get4WeekStartAndEndInISOString } from "../../utils/helpers/text";
import BookingGrid from "./BookingGrid";
import EquipmentBookingGrid from "./EquipmentBookingGrid";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  BOOKING_STORAGE_KEYS,
  GROUP_BOOKING_STORAGE_KEYS,
  getPersistedFilter,
  setPersistedFilter,
} from "../../utils/store/bookingFilterPersistence";
import { SharedFilterProvider } from "./SharedFilterContext";
import { generateViewKey, readForcedTimelineRangeFromStorage, resetViewState } from "./ViewSwitchingUtils";
import { withApollo } from "react-apollo";
import BookingList from "./BookingList";
import EquipmentBookingList from "./EquipmentBookingList";
import { getAllMasterFilterData } from "../equipment-list/LoadEquipmentService";
import { loadEquipmentGridColumns } from "../schema/helpers/equipmentGridColumns";
import { clearGetAllDataCache } from "../../utils/helpers/fetching";
import { getColumnObj, rehydrateStoredColumn } from "../equipment-list/EquipmentHelper";
import useTvMode from "../booking-instruments/useTvMode";
import moment from "moment/moment";
import {
  getInitialSelectedDateRange,
  getStorageOptions,
  getSubscriptionOperation,
  getViewContent,
} from "./Bookings.utils";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import {
  hasGroupViewPrefillParams,
  parseGroupViewPrefill,
  resolveGroupViewPrefillIdentities,
  buildPrefillInvalidMessage,
  buildPrefillWarningMessage,
} from "./groupViewPrefill.utils";
import { buildSavedFilterOptions } from "./BookingFilter.utils";

const serializeFilterModel = (model) => {
  try {
    return JSON.stringify(model || {});
  } catch (_error) {
    return "{}";
  }
};


const autoGroupColField = "equipment_model";

const MainViewStyled = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: ${(props) => (props.$isTvMode ? "0" : "8px")};
  padding-bottom: ${(props) => (props.$isTvMode ? "0" : "8px")};
  width: 100%;
  flex-grow: 1;
`;

function Bookings({
  subscriptionStatus,
  setSubscriptionStatus,
  setPopupEnableStatus,
  client,
  groupName,
  isGroupView = false,
}) {
  const getListModePreferenceKey = (groupByView) =>
    groupByView === LIST_GROUP_BY_BOOKING ? LIST_GROUP_BY_BOOKING : LIST_GROUP_BY_NONE;

  const normalizeSavedAdvanceFilter = (savedValue) => {
    if (savedValue && typeof savedValue === "object") {
      return {
        [LIST_GROUP_BY_BOOKING]: savedValue[LIST_GROUP_BY_BOOKING] ?? false,
        [LIST_GROUP_BY_NONE]: savedValue[LIST_GROUP_BY_NONE] ?? true,
      };
    }

    const fallback = typeof savedValue === "boolean" ? savedValue : true;
    return {
      [LIST_GROUP_BY_BOOKING]: fallback,
      [LIST_GROUP_BY_NONE]: fallback,
    };
  };

  const normalizeSavedShowFilters = (savedValue) => {
    if (savedValue && typeof savedValue === "object") {
      return {
        [GRID_VIEW]: savedValue[GRID_VIEW] ?? true,
        [LIST_VIEW]: savedValue[LIST_VIEW] ?? true,
        [TIMELINE_VIEW]: savedValue[TIMELINE_VIEW] ?? true,
      };
    }

    const fallback = typeof savedValue === "boolean" ? savedValue : true;
    return {
      [GRID_VIEW]: fallback,
      [LIST_VIEW]: fallback,
      [TIMELINE_VIEW]: fallback,
    };
  };

  const { trackPageView } = useMatomo();
  const { EventID } = useParams();
  const history = useHistory();
  const location = useLocation();
  const [eventID, setEventID] = useState(null);

  useEffect(() => {
    trackPageView({
      documentTitle: "Bookings",
      href: `${window.location?.origin}/Bookings`,
    });
    if (EventID) {
      setEventID(EventID);
      setView(GRID_VIEW);
    }
    // eslint-disable-next-line
  }, []);
  const [rowEquipmentId, setRowEquipmentId] = useState(null);
  const [shouldFetchData, triggerFetch] = useState(false);
  const [enableResetAll, setEnableResetAll] = useState(false);

  const user = useSelector((store) => store.user);
  const usersDirectory = useSelector((store) => store?.users?.users || []);
  const isTvMode = useSelector((state) => state?.bookingInstruments?.isTvMode);
  const [tvRefreshKey, setTvRefreshKey] = useState(0);

  const [resetAll, setResetAll] = useState(false);
  const storageKeys = isGroupView ? GROUP_BOOKING_STORAGE_KEYS : BOOKING_STORAGE_KEYS;
  const storageOptions = useMemo(() => getStorageOptions({ isGroupView, groupName }), [isGroupView, groupName]);
  const [showAdvanceFilterByList, setShowAdvanceFilterByList] = useState(() => {
    const savedValue = getPersistedFilter(storageKeys.SHOW_ADVANCE_FILTER, null, storageOptions);
    return normalizeSavedAdvanceFilter(savedValue);
  });
  const [disableResetAll, setDisableResetAll] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEquipment, setSelectedEquipment] = useState([]);

  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    return getInitialSelectedDateRange({
      user,
      get4WeekStartAndEndInISOString,
      readForcedTimelineRangeFromStorage,
      forceTimelineRangeKey: FORCE_TIMELINE_RANGE_KEY,
      moment,
    });
  });

  const [tempRowData, setTempRowData] = useState([]);
  const [dropType, setDropType] = useState("cancel"); // Default drop type
  const [isDropBooking, setIsDropBooking] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [openMultiDeleteDialog, setOpenMultiDeleteDialog] = useState(false);
  const { dispatchAction } = useContext(PaginationContext);
  const [status, setStatus] = useState(false);

  // Ref for calendar view to call methods imperatively
  const calendarRef = useRef(null);

  // States for hazardous/assistance approval workflow
  const [openHazardousRejectDialog, setOpenHazardousRejectDialog] = useState(false);
  const [hazardousBookingToReject, setHazardousBookingToReject] = useState(null);
  const [openAssistanceDialog, setOpenAssistanceDialog] = useState(false);
  const [bookingForAssistance, setBookingForAssistance] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [view, setView] = useState(() => {
    const defaultView = isGroupView ? TIMELINE_VIEW : LIST_VIEW;
    return getPersistedFilter(storageKeys.VIEW, defaultView, storageOptions);
  });
  const [isEquipmentView, setIsEquipmentView] = useState(() => {
    return getPersistedFilter(storageKeys.IS_EQUIPMENT_VIEW, false, storageOptions);
  });

  // Refs to track current view state for subscription callbacks (must be after state declarations)
  const currentViewRef = useRef(view);
  const isEquipmentViewRef = useRef(isEquipmentView);
  const [displayPreset, setDisplayPreset] = useState(() => {
    return getPersistedFilter(storageKeys.DISPLAY_PRESET, null, storageOptions);
  });
  const [listGroupByView, setListGroupByView] = useState(() => {
    return getPersistedFilter(storageKeys.LIST_GROUP_BY_VIEW, LIST_GROUP_BY_BOOKING, storageOptions);
  });
  const [savedFilter, setSavedFilter] = useState(() => {
    if (isGroupView && groupName) return groupName;
    return getPersistedFilter(storageKeys.SAVED_FILTER, MY_LAB, storageOptions);
  });
  const [signeeFilter, setSigneeFilter] = useState("");
  const [showFiltersByView, setShowFiltersByView] = useState(() => {
    const savedValue = getPersistedFilter(storageKeys.SHOW_FILTERS, null, storageOptions);
    return normalizeSavedShowFilters(savedValue);
  });
  const [innerTableFiltersByList, setInnerTableFiltersByList] = useState({});

  const isTimelineView = view === TIMELINE_VIEW;
  const isListGroupedByBookingView = view === LIST_VIEW && listGroupByView === LIST_GROUP_BY_BOOKING;
  const isListGroupedByNoneView =
    view === LIST_VIEW && (listGroupByView === LIST_GROUP_BY_NONE || listGroupByView === LIST_GROUP_BY_EQUIPMENT);
  const isAnyListView = isListGroupedByBookingView || isListGroupedByNoneView;
  const showFilters = showFiltersByView[view] ?? true;
  const setShowFilters = (nextValue) => {
    setShowFiltersByView((prevState) => {
      const previousValue = prevState[view] ?? true;
      const resolvedValue = typeof nextValue === "function" ? nextValue(previousValue) : nextValue;

      return {
        ...prevState,
        [view]: resolvedValue,
      };
    });
  };
  const listModePreferenceKey = getListModePreferenceKey(listGroupByView);
  const showAdvanceFilter = showAdvanceFilterByList[listModePreferenceKey] ?? true;
  const setShowAdvanceFilter = (nextValue) => {
    setShowAdvanceFilterByList((prevState) => {
      const previousValue = prevState[listModePreferenceKey] ?? true;
      const resolvedValue = typeof nextValue === "function" ? nextValue(previousValue) : nextValue;

      return {
        ...prevState,
        [listModePreferenceKey]: resolvedValue,
      };
    });
  };
  const [showInvalidFilter, setShowInvalidFilter] = useState(false);
  const [filterDataLoaded, setFilterDataLoaded] = useState(false);
  const groupAccessNotifiedRef = useRef(null);
  const [showSideBar, setShowSideBar] = useState(false);
  const [currentSideBarPanel, setCurrentSideBarPanel] = useState("");
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [isColumnActive, setIsColumnActive] = useState(false);
  const prefillToastKeyRef = useRef(null);
  const groupViewPrefillMemoryRef = useRef(null);
  const [groupViewPrefillResult, setGroupViewPrefillResult] = useState(null);

  const clearGroupViewPrefillState = useCallback(() => {
    groupViewPrefillMemoryRef.current = null;
    setGroupViewPrefillResult(null);
    prefillToastKeyRef.current = null;
    try {
      sessionStorage.removeItem(GROUP_VIEW_PREFILL_STORAGE_KEY);
    } catch (_e) {
      // sessionStorage may be unavailable; nothing else to clean up.
    }
  }, []);

  // Ensure the prefilled data is dropped for good if the user navigates away
  // (e.g. into the book-equipment wizard) instead of lingering in
  // sessionStorage and being incorrectly resurrected on a later visit.
  useEffect(() => {
    return () => {
      try {
        sessionStorage.removeItem(GROUP_VIEW_PREFILL_STORAGE_KEY);
      } catch (_e) {
        // sessionStorage may be unavailable; nothing else to clean up.
      }
    };
  }, []);

  useEffect(() => {
    if (!isGroupView) {
      // Navigated away from group views entirely (e.g. "All bookings" / "My bookings").
      clearGroupViewPrefillState();
      return;
    }

    if (hasGroupViewPrefillParams(location.search)) {
      const parsed = parseGroupViewPrefill(location.search);
      const resolvedPrefillResult = {
        ...parsed,
        warnings: {
          unknownUsers: [],
          unknownAssistant: null,
        },
      };

      if (parsed.isValid && usersDirectory.length > 0) {
        const identityResult = resolveGroupViewPrefillIdentities(parsed.normalizedPrefill, usersDirectory);
        resolvedPrefillResult.normalizedPrefill = identityResult.normalizedPrefill;
        resolvedPrefillResult.warnings = identityResult.warnings;
      }

      groupViewPrefillMemoryRef.current = { pathname: location.pathname, result: resolvedPrefillResult };
      setGroupViewPrefillResult(resolvedPrefillResult);
      if (resolvedPrefillResult.isValid && view !== TIMELINE_VIEW) {
        // Force timeline view so the prefilled booking feature is actually
        // usable, even if the user's persisted view preference is grid/list.
        setView(TIMELINE_VIEW);
      }

      try {
        if (resolvedPrefillResult.isValid) {
          sessionStorage.setItem(
            GROUP_VIEW_PREFILL_STORAGE_KEY,
            JSON.stringify({ pathname: location.pathname, normalizedPrefill: resolvedPrefillResult.normalizedPrefill })
          );
        } else {
          sessionStorage.removeItem(GROUP_VIEW_PREFILL_STORAGE_KEY);
        }
      } catch (_e) {
        // sessionStorage may be unavailable; prefill still works from URL.
      }

      history.replace({
        pathname: location.pathname,
        hash: location.hash,
      });
      return;
    }

    // No prefill params on this navigation. Only reuse a previously captured prefill
    // (in-memory or persisted) when it was captured for this exact route/group view -
    // otherwise navigating to a different group view (or back to this one after the
    // prefill flow has ended) would incorrectly resurrect stale data.
    const memoryEntry = groupViewPrefillMemoryRef.current;
    if (memoryEntry && memoryEntry.pathname === location.pathname) {
      setGroupViewPrefillResult(memoryEntry.result);
      if (memoryEntry.result?.isValid && view !== TIMELINE_VIEW) {
        setView(TIMELINE_VIEW);
      }
      return;
    }

    if (memoryEntry && memoryEntry.pathname !== location.pathname) {
      // Switched from one group view to a different one while a prefill was staged -
      // it no longer applies here, so drop it.
      clearGroupViewPrefillState();
      return;
    }

    groupViewPrefillMemoryRef.current = null;

    try {
      const rawPrefill = sessionStorage.getItem(GROUP_VIEW_PREFILL_STORAGE_KEY);
      if (!rawPrefill) {
        setGroupViewPrefillResult(null);
        return;
      }

      const persisted = JSON.parse(rawPrefill);
      if (!persisted || persisted.pathname !== location.pathname) {
        sessionStorage.removeItem(GROUP_VIEW_PREFILL_STORAGE_KEY);
        setGroupViewPrefillResult(null);
        return;
      }

      let normalizedPrefill = persisted.normalizedPrefill;
      let warnings = {
        unknownUsers: [],
        unknownAssistant: null,
      };

      if (usersDirectory.length > 0) {
        const identityResult = resolveGroupViewPrefillIdentities(persisted.normalizedPrefill, usersDirectory);
        normalizedPrefill = identityResult.normalizedPrefill;
        warnings = identityResult.warnings;
      }

      const persistedResult = {
        isValid: true,
        errors: [],
        normalizedPrefill,
        warnings,
      };

      groupViewPrefillMemoryRef.current = { pathname: location.pathname, result: persistedResult };
      setGroupViewPrefillResult(persistedResult);
      if (view !== TIMELINE_VIEW) {
        setView(TIMELINE_VIEW);
      }
    } catch (_e) {
      setGroupViewPrefillResult(null);
    }
  }, [history, isGroupView, location.hash, location.pathname, location.search, usersDirectory, clearGroupViewPrefillState, view]);

  const normalizedGroupViewPrefill = groupViewPrefillResult?.isValid
    ? groupViewPrefillResult.normalizedPrefill
    : null;

  const getDefaultEquipmentBasedColumns = () => {
    if (listGroupByView !== LIST_GROUP_BY_NONE) return DEFAULT_COLUMNS();
    const defaultCols = structuredClone(DEFAULT_COLUMNS());
    defaultCols.displayDefinition.showColumns = EQUIPMENT_BASED_DEFAULT_SHOW_COLUMNS;
    return defaultCols;
  };
  const [dataModel, setDataModel] = useState([]);
  const [myLabFilter, setMyLabFilter] = useState(null);
  const [masterDataFilters, setMasterDataFilters] = useState({});
  const [userDisplayColumnMaster, setUserDisplayColumnMaster] = useState([]);
  const [userFilters, setUserFilters] = useState([]);
  const [lastFavFilter, setLastFavFilter] = useState(DEFAULT_FILTER);
  const [lastDisplayColumns, setLastDisplayColumns] = useState(() => getDefaultEquipmentBasedColumns());
  const localUserFilters = useRef(userFilters);
  const [defaultShowColumns, setDefaultShowColumns] = useState(() => getDefaultEquipmentBasedColumns());

  useEffect(() => {
    localUserFilters.current = userFilters;
  }, [userFilters]);

  const loadFilterData = useCallback(async () => {
    if (!user?.site || !user?.id) return;

    // Resolve the schema-derived columns before the grid renders. Until this settles, allTableColumn
    // and the listOf… constants answer from their hand-written fallbacks.
    await loadEquipmentGridColumns().catch((error) => {
      // Loud on purpose. A failure here is not cosmetic: allTableColumn() falls back to the
      // hand-written list, so every schema-only column silently disappears from the grid and the
      // columns tool panel. The fallback keeps the page usable, but the cause must be findable.
      // eslint-disable-next-line no-console
      console.error(
        "[schema] Could not derive grid columns; falling back to the hand-written list. " +
          "Schema-only columns (Description, Equipment type, …) will be missing.",
        error
      );
    });

    try {
      const { masterFiltersList, myLabFilterMaster, userDetail, userDisplayColumnMaster } =
        await getAllMasterFilterData({
          client: client,
          site: user.site,
          userID: user.id,
          cachedUser: user,
        });

      setMyLabFilter(myLabFilterMaster);
      setMasterDataFilters(() => masterFiltersList);
      setUserDisplayColumnMaster(userDisplayColumnMaster);
      setLastFavFilter(() => userDetail?.lastFavFilter);
      setLastDisplayColumns(() => userDetail?.lastDisplayColumns);
      const withFilterId = (userDetail?.userFilters || []).map((filter) => {
        return {
          ...filter,
          filterDefinition: {
            ...filter?.filterDefinition,
            filterId: `f_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
          },
        };
      });

      setUserFilters(withFilterId);

      const defaultShowCols = filter(allTableColumn(listGroupByView === LIST_GROUP_BY_NONE), { show: true });
      setDefaultShowColumns(() => {
        return defaultShowCols?.map((value) => {
          return getColumnObj(value, [], convertToCameCase(autoGroupColField)); // TODO
        });
      });
      setFilterDataLoaded(true);
    } catch (error) {
      console.error("Error loading filter data:", error);
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  const { createBookingCallback } = useBookingOperations();

  const rejectBookingCallback = createBookingCallback({
    operation: BOOKING_OPERATIONS.REJECT,
    resetStates: {
      multiDeleteDialog: setOpenMultiDeleteDialog,
      status: setStatus,
    },
    clearSelections: [setTempRowData],
    onSuccess: () => {
      if (dispatchAction) {
        clearGetAllDataCache();
        dispatchAction({ type: "fetching", payload: false });
        dispatchAction({ type: "clearAll" });
      }
    },
  });

  useTvMode({
    isTimelineView,
    calendarRef,
    onTick: () => setTvRefreshKey((key) => key + 1),
    deps: [selectedDateRange?.[0], selectedDateRange?.[1], savedFilter, listGroupByView, signeeFilter?.userId],
  });

  useEffect(() => {
    if (isTvMode && view !== TIMELINE_VIEW) {
      setView(TIMELINE_VIEW);
    }
  }, [isTvMode, view]);

  useEffect(() => {
    if (!isGroupView || !groupViewPrefillResult) return;

    if (view !== TIMELINE_VIEW) {
      // Let the user freely switch views; the staged prefill no longer applies
      // once they leave the timeline view, so drop it instead of forcing them
      // back.
      clearGroupViewPrefillState();
      return;
    }

    if (!groupViewPrefillResult.isValid) {
      const invalidToastKey = `invalid:${location.pathname}:${JSON.stringify(groupViewPrefillResult.errors || [])}`;
      if (prefillToastKeyRef.current === invalidToastKey) {
        return;
      }

      snackbarService.show({
        message: buildPrefillInvalidMessage(groupViewPrefillResult.errors),
        type: "error",
        duration: DURATION,
      });
      prefillToastKeyRef.current = invalidToastKey;
      return;
    }

    const hasUnresolvedIdentities =
      (groupViewPrefillResult.warnings?.unknownUsers?.length > 0) ||
      Boolean(groupViewPrefillResult.warnings?.unknownAssistant);

    const toastKey = `${location.pathname}:${JSON.stringify(groupViewPrefillResult.normalizedPrefill || {})}:${JSON.stringify(groupViewPrefillResult.warnings || {})}`;
    if (prefillToastKeyRef.current === toastKey) {
      return;
    }

    if (hasUnresolvedIdentities) {
      snackbarService.show({
        message: buildPrefillWarningMessage(groupViewPrefillResult.warnings),
        type: "error",
        duration: DURATION,
      });
      prefillToastKeyRef.current = toastKey;
      return;
    }

    snackbarService.show({
      message: "Your booking is pre populated, please select a slot",
      type: "success",
      duration: DURATION,
    });
    prefillToastKeyRef.current = toastKey;
  }, [isGroupView, groupViewPrefillResult, location.pathname, view, clearGroupViewPrefillState]);

  // Effect to handle invalid filter display with delay. A group view is
  // invalid when `groupName` doesn't match any of the user's actual
  // accessible filters (i.e. the group doesn't exist, or the user has no
  // access to it) -- comparing `groupName !== savedFilter` alone is not
  // enough because `savedFilter` is initialized to `groupName` on mount
  // regardless of validity, so they're never different on a direct/first
  // navigation to a bad group-view URL.
  const groupViewSavedFilterOptions = useMemo(
    () => buildSavedFilterOptions({ myLabFilter, userFilters, signeeFilter }),
    [myLabFilter, userFilters, signeeFilter],
  );

  useEffect(() => {
    if (!isGroupView || !groupName || !filterDataLoaded) {
      setShowInvalidFilter(false);
      return;
    }

    const groupExists = groupViewSavedFilterOptions.some((option) => option.name === groupName);

    if (!groupExists || groupName !== savedFilter) {
      const timeoutId = setTimeout(() => {
        setShowInvalidFilter(true);
        if (!groupExists && groupAccessNotifiedRef.current !== groupName) {
          groupAccessNotifiedRef.current = groupName;
          snackbarService.show({
            message: `You don't have access to "${groupName}", or this group view doesn't exist.`,
            type: "error",
            duration: DURATION,
          });
        }
      }, 2000); // 2 second delay

      return () => clearTimeout(timeoutId);
    }

    groupAccessNotifiedRef.current = null;
    setShowInvalidFilter(false);
  }, [groupName, savedFilter, isGroupView, filterDataLoaded, groupViewSavedFilterOptions]);

  // Improved localStorage persistence with batching
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      const filtersToSave = {
        [storageKeys.SAVED_FILTER]: savedFilter,
        [storageKeys.SHOW_FILTERS]: showFiltersByView,
        [storageKeys.SHOW_ADVANCE_FILTER]: showAdvanceFilterByList,
        [storageKeys.VIEW]: view,
        [storageKeys.IS_EQUIPMENT_VIEW]: isEquipmentView,
        [storageKeys.LIST_GROUP_BY_VIEW]: listGroupByView,
        [storageKeys.DISPLAY_PRESET]: displayPreset,
      };

      // Batch localStorage updates to prevent multiple DOM writes
      Object.entries(filtersToSave).forEach(([key, value]) => {
        try {
          setPersistedFilter(key, value, storageOptions);
        } catch (error) {
          console.warn("Failed to persist filter:", key, error);
        }
      });
    }, 100); // Small delay to batch rapid changes

    return () => clearTimeout(timeoutId); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    savedFilter,
    signeeFilter?.userId,
    showFiltersByView,
    showAdvanceFilterByList,
    searchQuery,
    displayPreset,
    selectedDateRange,
    view,
    isEquipmentView,
    listGroupByView,
    isGroupView,
    storageOptions,
  ]);

  useEffect(() => {
    currentViewRef.current = view;
  }, [view]);

  useEffect(() => {
    isEquipmentViewRef.current = isEquipmentView;
  }, [isEquipmentView]);

  // Memoize the booking callback to prevent recreation
  const bookingCallback = useMemo(() => {
    return (data) => {
      const subscriptionData = data?.data?.onUpdateBookingCommand;
      const currentView = currentViewRef.current;

      if (!subscriptionData) return;

      const processingResult = subscriptionData.processingResult;
      const commandId = subscriptionData.id;

      if (currentView === "GRID") {
        if (!calendarRef.current) {
          setTimeout(() => {
            if (calendarRef.current) {
              bookingCallback(data);
            } else {
              console.error("[Bookings] calendarRef still null after timeout");
            }
          }, 100);
          return;
        }

        const gridOperation = getSubscriptionOperation({
          processingResult,
          commandId,
          commandType: subscriptionData.commandType,
          quickEditTracker,
          BOOKING_OPERATIONS,
        });

        calendarRef.current.handleSubscriptionEvent(data, gridOperation);
        return;
      }

      const listOperation = getSubscriptionOperation({
        processingResult,
        commandId,
        commandType: subscriptionData.commandType,
        quickEditTracker,
        BOOKING_OPERATIONS,
      });

      const callback = createBookingCallback({
        operation: listOperation,
        resetStates: {
          multiDeleteDialog: setOpenMultiDeleteDialog,
          status: setStatus,
          dropBooking: setIsDropBooking,
          dialog: setIsDialogOpen,
          hazardousRejectDialog: setOpenHazardousRejectDialog,
          assistanceDialog: setOpenAssistanceDialog,
          editMode: setIsEditMode,
        },
        clearSelections: [setTempRowData],
        triggerRefresh: () => {
          triggerFetch(false);
          triggerFetch(true);
        },
        onSuccess: (data) => {
          const commandId = data?.data?.onUpdateBookingCommand?.id;
          const currentViewInCallback = currentViewRef.current;

          if (currentViewInCallback === "GRID" && quickEditTracker.isQuickEdit(commandId)) {
            return;
          }

          setHazardousBookingToReject(null);
          setBookingForAssistance(null);
          if (dispatchAction) {
            clearGetAllDataCache();
            dispatchAction({ type: "fetching", payload: false });
            dispatchAction({ type: "clearAll" });
          }
        },
      });

      callback(data);
    };
  }, [createBookingCallback, triggerFetch, dispatchAction]);

  // Handle subscription status notifications
  useBookingSubscriptionStatus(subscriptionStatus, setSubscriptionStatus);

  useEffect(() => {
    const unorderedHidenColumn = [];
    const orderedShowColumns = sortBy(
      convertStrToObjColumns(lastDisplayColumns)?.displayDefinition?.showColumns,
      ["order"],
      ["asc"],
    );

    allTableColumn(listGroupByView === LIST_GROUP_BY_NONE)?.forEach((value) => {
      const obj = find(orderedShowColumns, {
        key: value?.key,
      });
      if (!obj) {
        unorderedHidenColumn.push(value);
      }
    });

    // A stored entry is a layout record, not a column definition — see rehydrateStoredColumn.
    const schemaColumns = keyBy(allTableColumn(listGroupByView === LIST_GROUP_BY_NONE), "key");

    const redefinedColumns = [...orderedShowColumns, ...unorderedHidenColumn]?.map((value) => {
      return getColumnObj(
        rehydrateStoredColumn(schemaColumns, value),
        masterDataFilters,
        convertToCameCase(autoGroupColField),
      );
    });
    const defaultShowCols = filter(allTableColumn(listGroupByView === LIST_GROUP_BY_NONE), { show: true });
    setDefaultShowColumns(() => {
      return defaultShowCols?.map((value) => {
        return getColumnObj(value, masterDataFilters, convertToCameCase(autoGroupColField));
      });
    });
    setDataModel([...redefinedColumns]);
    // eslint-disable-next-line
  }, [masterDataFilters]);
  // Memoize view display functions to prevent recreation
  const listViewDisplay = useCallback(() => {
    const innerTableFilterKey = getListModePreferenceKey(listGroupByView);
    const componentKey = generateViewKey(
      view,
      true,
      savedFilter,
      signeeFilter?.userId || "all",
      listGroupByView || LIST_GROUP_BY_BOOKING,
      displayPreset?.label || "none",
      currentSideBarPanel || "none",
    );
    const listCommonProps = {
      setEnableResetAll,
      selectedDateRange,
      setDisableResetAll,
      setSelectedDateRange,
      setSelectedEquipment,
      selectedEquipment,
      showAdvanceFilter,
      resetAll,
      isGroupView,
      setSearchQuery,
      searchQuery,
      setResetAll,
      setDropType,
      setIsDropBooking,
      setIsDialogOpen,
      setOpenMultiDeleteDialog,
      setRowEquipmentId,
      tempRowData,
      setTempRowData,
      setPopupEnableStatus,
      rejectBookingCallback,
      setOpenHazardousRejectDialog,
      setHazardousBookingToReject,
      setOpenAssistanceDialog,
      setBookingForAssistance,
      setIsEditMode,
      openHazardousRejectDialog,
      hazardousBookingToReject,
      openAssistanceDialog,
      bookingForAssistance,
      isEditMode,
      listGroupByView,
      signeeFilter,
      setSigneeFilter,
      displayPreset,
      dataModel,
      setDisplayPreset,
      defaultShowColumns,
      shouldFetchData,
      userFilters,
      setUserFilters,
      lastDisplayColumns,
      setLastDisplayColumns,
      lastFavFilter,
      setLastFavFilter,
      setSavedFilter,
      userDisplayColumnMaster,
      setUserDisplayColumnMaster,
      isColumnActive,
      setIsColumnActive,
      localUserFilters,
      savedFilter,
      myLabFilter,
      setShowSideBar,
      showSideBar,
      currentSideBarPanel,
      setCurrentSideBarPanel,
      isFilterActive,
      setIsFilterActive,
      innerTableFilters: innerTableFiltersByList?.[innerTableFilterKey] || {},
      setInnerTableFilters: (nextFilters) => {
        setInnerTableFiltersByList((prev) => {
          const previousFilters = prev?.[innerTableFilterKey] || {};
          const normalizedNextFilters = nextFilters || {};

          if (serializeFilterModel(previousFilters) === serializeFilterModel(normalizedNextFilters)) {
            return prev;
          }

          return {
            ...(prev || {}),
            [innerTableFilterKey]: normalizedNextFilters,
          };
        });
      },
    };
    if (listGroupByView === LIST_GROUP_BY_NONE || listGroupByView === LIST_GROUP_BY_EQUIPMENT) {
      return <EquipmentBookingList key={componentKey} {...listCommonProps} />;
    }
    return <BookingList key={componentKey} {...listCommonProps} />; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isGroupView,
    listGroupByView,
    savedFilter,
    signeeFilter?.userId,
    showFilters,
    tempRowData,
    displayPreset,
    showAdvanceFilter,
    dataModel, // eslint-disable-next-line react-hooks/exhaustive-deps
    setIsEquipmentView,
    setSavedFilter,
    setSigneeFilter,
    shouldFetchData,
    setShowFilters,
    selectedDateRange,
    selectedEquipment,
    searchQuery,
    showSideBar,
  ]);

  const calendarViewDisplay = useCallback(() => {
    const componentKey = generateViewKey(
      view,
      isEquipmentView,
      savedFilter,
      signeeFilter?.userId || "all",
      listGroupByView || LIST_GROUP_BY_BOOKING,
      displayPreset?.label || "none",
      currentSideBarPanel || "none",
      isTimelineView,
    );
    const calendarCommonProps = {
      eventID,
      isEquipmentView,
      setIsEquipmentView,
      setPopupEnableStatus,
      savedFilter,
      setSavedFilter,
      signeeFilter,
      setSigneeFilter,
      showFilters,
      setShowFilters,
      isGroupView,
      view,
      listGroupByView,
      setListGroupByView,
      isTimelineView,
      tvRefreshKey,
      timelineGroupByDefault:
        isGroupView && (groupName || "").toLowerCase() === MY_LAB.toLowerCase() ? "equipment" : "category",
      timelineGroupByStorageKey: storageKeys.TIMELINE_GROUP_BY_VIEW,
      timelineTimeframeStorageKey: storageKeys.TIMELINE_TIMEFRAME_VIEW,
      timelineStorageOptions: storageOptions,
      groupViewPrefill: normalizedGroupViewPrefill,
    };
    if (isTimelineView) {
      return (
        <EquipmentBookingGrid
          ref={calendarRef}
          key={`${componentKey}__tv_${tvRefreshKey}`}
          {...calendarCommonProps}
          selectedDateRange={selectedDateRange}
        />
      );
    }
    return <BookingGrid ref={calendarRef} key={componentKey} {...calendarCommonProps} />; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    tvRefreshKey,
    eventID,
    isEquipmentView,
    isTimelineView,
    setPopupEnableStatus,
    savedFilter,
    signeeFilter?.userId,
    showFilters,
    showAdvanceFilter, // eslint-disable-next-line react-hooks/exhaustive-deps
    setIsEquipmentView,
    setSavedFilter,
    setSigneeFilter,
    setShowFilters,
    selectedDateRange,
    isGroupView,
    listGroupByView,
    displayPreset?.label,
    currentSideBarPanel,
    normalizedGroupViewPrefill,
  ]);

  useEffect(() => {
    if (view === "GRID") {
      setIsEquipmentView(listGroupByView === TIMELINE_VIEW);
    }
  }, [view, listGroupByView]);

  useEffect(() => {
    // Reset state when switching views to ensure clean data load
    resetViewState(
      {
        setTempRowData,
        setSelectedEquipment,
      },
      dispatchAction,
    );

    // Log view switch for debugging
    // logViewSwitch(view, isEquipmentView, savedFilter, signeeFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    isEquipmentView,
    listGroupByView,
    savedFilter,
    signeeFilter?.userId,
    setTempRowData,
    setSelectedEquipment,
    dispatchAction,
  ]);

  const shouldRenderViews = !showInvalidFilter || !isGroupView;
  const viewContent = getViewContent({
    shouldRenderViews,
    isAnyListView,
    listViewDisplay,
    calendarViewDisplay,
    invalidFilterText: INVALID_FILTER,
  });

  return (
    <SharedFilterProvider client={client} user={user}>
      <MainViewStyled $isTvMode={isTvMode}>
        <>
          <BookingFilter
            resetAll={resetAll}
            setEnableResetAll={setEnableResetAll}
            enableResetAll={enableResetAll}
            setResetAll={setResetAll}
            showAdvanceFilter={showAdvanceFilter}
            setShowAdvanceFilter={setShowAdvanceFilter}
            setSelectedEquipment={setSelectedEquipment}
            selectedDateRange={selectedDateRange}
            setSelectedDateRange={setSelectedDateRange}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            tempRowData={tempRowData}
            setTempRowData={setTempRowData}
            disableResetAll={disableResetAll}
            setDisableResetAll={setDisableResetAll}
            view={view}
            setView={setView}
            isEquipmentView={isEquipmentView || isTimelineView}
            isTimelineView={isTimelineView}
            isTvMode={isTvMode}
            setIsEquipmentView={setIsEquipmentView}
            listGroupByView={listGroupByView}
            setListGroupByView={setListGroupByView}
            savedFilter={savedFilter}
            setSavedFilter={setSavedFilter}
            signeeFilter={signeeFilter}
            setSigneeFilter={setSigneeFilter}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            groupName={groupName}
            isGroupView={isGroupView}
            displayPreset={displayPreset}
            setDisplayPreset={setDisplayPreset}
            showInvalidFilter={showInvalidFilter}
            storageKeys={storageKeys}
            storageOptions={storageOptions}
            userFilters={userFilters}
            userDisplayColumnMaster={userDisplayColumnMaster}
            myLabFilter={myLabFilter}
            showSideBar={showSideBar}
            setShowSideBar={setShowSideBar}
            currentSideBarPanel={currentSideBarPanel}
            setCurrentSideBarPanel={setCurrentSideBarPanel}
            isFilterActive={isFilterActive}
            setIsFilterActive={setIsFilterActive}
            isColumnActive={isColumnActive}
            setIsColumnActive={setIsColumnActive}
          />

          {viewContent}
          <BookingNotificationStatus
            tryTimes={3}
            timeout={5 * 60 * 1000}
            onTimeoutCallback={timeoutCallbackFactory()}
            onBookingCallback={bookingCallback}
          />
          {isDropBooking && (
            <DeleteBooking
              dropType={dropType}
              setIsDropBooking={setIsDropBooking}
              setIsDialogOpen={setIsDialogOpen}
              isDialogOpen={isDialogOpen}
              item={rowEquipmentId}
              status={status}
              setStatus={setStatus}
              triggerFetch={triggerFetch}
            />
          )}
          {openMultiDeleteDialog && (
            <MyBookingMultiDeleteDialog
              openMultiDeleteDialog={openMultiDeleteDialog}
              setOpenMultiDeleteDialog={setOpenMultiDeleteDialog}
              selectedEquipment={selectedEquipment}
              setSelectedEquipment={setSelectedEquipment}
              status={status}
              setStatus={setStatus}
            />
          )}
        </>
      </MainViewStyled>
    </SharedFilterProvider>
  );
}

export default withApollo(Bookings);
